import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { Icon } from '@luacentrix/icons';
import { cn } from './utils';
import { place } from './floating';
import { Button, IconButton } from './primitives';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

function useFocusTrap(
  active: boolean,
  container: RefObject<HTMLElement | null>,
  onDismiss: () => void,
) {
  const dismiss = useRef(onDismiss);

  useEffect(() => {
    dismiss.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!active) return;
    const previous = document.activeElement as HTMLElement | null;
    const node = container.current;
    const first = node?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        dismiss.current();
        return;
      }
      if (event.key !== 'Tab' || node === null) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (firstItem === undefined || lastItem === undefined) return;
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [active, container]);
}

export interface DialogProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  role?: 'dialog' | 'alertdialog';
}

export function Dialog({
  open,
  title,
  description,
  children,
  footer,
  onClose,
  role = 'dialog',
}: DialogProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  useFocusTrap(open, ref, onClose);

  if (!open) return null;

  return (
    <div
      className="lc-backdrop"
      onPointerDown={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description === undefined ? undefined : descriptionId}
        className="lc-modal"
      >
        <header className="lc-modal__header">
          <h2 id={titleId} className="lc-modal__title">
            {title}
          </h2>
          <IconButton name="x" label="Close dialog" onClick={onClose} />
        </header>
        <div className="lc-modal__body">
          {description === undefined ? null : <p id={descriptionId}>{description}</p>}
          {children}
        </div>
        {footer === undefined ? null : <footer className="lc-modal__footer">{footer}</footer>}
      </div>
    </div>
  );
}

export interface AlertDialogProps {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function AlertDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
  onConfirm,
  onClose,
}: AlertDialogProps) {
  return (
    <Dialog
      open={open}
      role="alertdialog"
      title={title}
      description={description}
      onClose={loading ? () => undefined : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}

export function Drawer({
  open,
  title,
  children,
  onClose,
  side = 'left',
}: {
  open: boolean;
  title: string;
  children?: ReactNode;
  onClose: () => void;
  side?: 'left' | 'right';
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(open, ref, onClose);
  if (!open) return null;
  return (
    <>
      <div
        className="lc-backdrop"
        style={{ placeItems: 'stretch' }}
        onPointerDown={event => {
          if (event.target === event.currentTarget) onClose();
        }}
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="lc-drawer"
        style={
          side === 'right'
            ? {
                inset: '0 0 0 auto',
                borderRight: 0,
                borderLeft: '1px solid var(--lc-border-default)',
              }
            : undefined
        }
      >
        <header className="lc-modal__header">
          <h2 id={titleId} className="lc-modal__title">
            {title}
          </h2>
          <IconButton name="x" label="Close panel" onClick={onClose} />
        </header>
        <div className="lc-stack lc-stack--loose" style={{ marginTop: 'var(--lc-space-8)' }}>
          {children}
        </div>
      </div>
    </>
  );
}

export function Popover({
  open,
  anchor,
  onClose,
  children,
  label,
}: {
  open: boolean;
  anchor: DOMRect | null;
  onClose: () => void;
  children: ReactNode;
  label: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (ref.current?.contains(event.target as Node) !== true) onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open || anchor === null) return null;

  const computed = place(
    {
      top: anchor.top,
      left: anchor.left,
      width: anchor.width,
      bottom: anchor.bottom,
      right: anchor.right,
    },
    { width: 300, height: 200 },
    { width: window.innerWidth, height: window.innerHeight },
  );

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={label}
      className="lc-popover"
      style={{ top: computed.top, left: computed.left }}
    >
      {children}
    </div>
  );
}

export function Tooltip({ text, children }: { text: string; children: ReactNode }) {
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const id = useId();

  const show = useCallback((target: HTMLElement) => {
    const rect = target.getBoundingClientRect();
    const above = rect.top - 30;
    setCoords({
      top: above < 8 ? rect.bottom + 8 : above,
      left: Math.min(Math.max(rect.left + rect.width / 2, 8), window.innerWidth - 8),
    });
  }, []);

  return (
    <span
      style={{ display: 'inline-flex' }}
      onPointerEnter={event => show(event.currentTarget)}
      onPointerLeave={() => setCoords(null)}
      onFocus={event => show(event.currentTarget)}
      onBlur={() => setCoords(null)}
    >
      <span aria-describedby={coords === null ? undefined : id}>{children}</span>
      {coords === null ? null : (
        <span
          id={id}
          role="tooltip"
          className="lc-tooltip"
          style={{ top: coords.top, left: coords.left, transform: 'translateX(-50%)' }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

export type ToastTone = 'neutral' | 'success' | 'warning' | 'danger';

export interface ToastRecord {
  id: string;
  tone: ToastTone;
  title: string;
  body?: string;
}

const toastListeners = new Set<(records: Array<ToastRecord>) => void>();
let toastRecords: Array<ToastRecord> = [];

export function pushToast(record: Omit<ToastRecord, 'id'>): void {
  const id = `${Date.now()}-${toastRecords.length}`;
  toastRecords = [...toastRecords, { ...record, id }];
  for (const listener of toastListeners) listener(toastRecords);
  window.setTimeout(() => dismissToast(id), 5000);
}

export function dismissToast(id: string): void {
  toastRecords = toastRecords.filter(record => record.id !== id);
  for (const listener of toastListeners) listener(toastRecords);
}

const toastIcons = {
  neutral: 'bell',
  success: 'check',
  warning: 'alertTriangle',
  danger: 'alertCircle',
} as const;

export function ToastRegion({ className }: { className?: string }) {
  const [records, setRecords] = useState<Array<ToastRecord>>(toastRecords);

  useEffect(() => {
    toastListeners.add(setRecords);
    return () => {
      toastListeners.delete(setRecords);
    };
  }, []);

  return (
    <div className={cn('lc-toast-region', className)} role="region" aria-label="Notifications">
      <div aria-live="polite" aria-atomic="false" className="lc-sr-only-live">
        {records.map(record => (
          <p
            key={record.id}
          >{`${record.title}${record.body === undefined ? '' : `. ${record.body}`}`}</p>
        ))}
      </div>
      {records.map(record => (
        <article key={record.id} className={cn('lc-toast', `lc-toast--${record.tone}`)}>
          <Icon name={toastIcons[record.tone]} size="md" />
          <div style={{ flex: 1 }}>
            <p className="lc-toast__title">{record.title}</p>
            {record.body === undefined ? null : <p className="lc-toast__body">{record.body}</p>}
          </div>
          <IconButton
            name="x"
            label="Dismiss notification"
            onClick={() => dismissToast(record.id)}
          />
        </article>
      ))}
    </div>
  );
}
